// @ts-check
/**
 * src/lib/cookie-consent.js
 *
 * Helper para leer y actualizar el consentimiento de cookies del user.
 * Compatible con el formato persistido por CookieConsentBanner.js.
 *
 * Storage key: `dr_victor_cookie_consent`
 * Formato:
 * {
 *   version: '1.1',
 *   accepted_at: ISO,
 *   essential: true,         // siempre true (cookies de sesión no son opt-in)
 *   marketing: false,        // controla Meta/TikTok/GA4 pixels + CAPI
 * }
 *
 * MODELO OPT-OUT (2026-06): el hook `useMarketingConsent` devuelve `true`
 * por DEFAULT (tráfico 100% RD, Ley 172-13, no GDPR). El pixel dispara salvo
 * que el user RECHACE explícitamente. La forma persistida sigue guardando la
 * decisión real del user (true/false) para respetar rechazos.
 *
 * BACK-COMPAT: si el user aceptó con la versión 1.0 (que no tenía
 * `marketing`), lo tratamos como `marketing: false` (respeta su decisión vieja).
 * El user verá el banner nuevamente cuando bumpeemos POLICY_VERSION a 1.1
 * en CookieConsentBanner.
 */

import { useEffect, useState } from 'react';

export const STORAGE_KEY = 'dr_victor_cookie_consent';
export const POLICY_VERSION = '1.1';

/**
 * Lee el consentimiento actual del localStorage. SSR-safe (devuelve null
 * en server). Si no hay consent o el formato es viejo, devuelve null y
 * el banner volverá a aparecer.
 */
export function readConsent() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Solo aceptamos el formato versión actual o 1.0 (back-compat).
    if (parsed?.version !== POLICY_VERSION && parsed?.version !== '1.0') {
      return null;
    }
    return {
      version: parsed.version,
      accepted_at: parsed.accepted_at || null,
      essential: true,
      // Default deny: si la versión es 1.0 (sin marketing field), marketing=false.
      // El user vuelve a ver el banner por el version bump.
      marketing: parsed.version === POLICY_VERSION ? !!parsed.marketing : false,
    };
  } catch {
    return null;
  }
}

/**
 * Persiste el consentimiento del user. Llamar desde CookieConsentBanner
 * cuando el user acepta/rechaza.
 * @param {{ marketing?: boolean }} consent
 */
export function writeConsent({ marketing }) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({
      version: POLICY_VERSION,
      accepted_at: new Date().toISOString(),
      essential: true,
      marketing: !!marketing,
    }));
    // Emit custom event para que pixels que ya están montados se inicialicen
    // sin esperar al próximo route change.
    window.dispatchEvent(new CustomEvent('dv:consent-change', {
      detail: { marketing: !!marketing },
    }));
  } catch { /* private mode, no-op */ }
}

/**
 * Hook React que devuelve `true` si el user aceptó cookies de marketing.
 * Re-renderiza automáticamente cuando el consentimiento cambia (via el
 * evento `dv:consent-change` emitido por writeConsent).
 *
 * Útil para gatear el render de MetaPixel/TikTokPixel/GoogleAnalytics:
 *
 *   const allowed = useMarketingConsent();
 *   if (!allowed) return null;
 */
export function useMarketingConsent() {
  // MODELO OPT-OUT (2026-06): para tráfico de República Dominicana
  // (Ley 172-13, NO GDPR), el pixel de marketing dispara POR DEFAULT y solo
  // se desactiva si el user RECHAZA explícitamente en el banner.
  //
  // ANTES era opt-in (default deny): el pixel no disparaba hasta que el user
  // clickeaba "aceptar". Resultado: ~95% del tráfico de ads NO se trackeaba
  // (los users no aceptan banners), Meta optimizaba a ciegas y no se podían
  // medir conversiones ni armar retargeting. La audiencia es 100% RD, donde
  // el modelo opt-out es legal y estándar.
  //
  // SSR-safe: useState(true) renderiza igual en server y client (no lee
  // localStorage en el initializer) → sin hydration mismatch. El useEffect
  // corrige a false solo para quienes rechazaron explícitamente.
  const [allowed, setAllowed] = useState(true);

  useEffect(() => {
    const update = () => {
      const c = readConsent();
      // Sin decisión guardada → permitido (opt-out). Con decisión → respetarla.
      setAllowed(c ? !!c.marketing : true);
    };
    update();
    const onChange = (/** @type {Event} */ e) => setAllowed(!!(/** @type {CustomEvent} */ (e)).detail?.marketing);
    window.addEventListener('dv:consent-change', onChange);
    // También escuchamos storage events para sync entre pestañas.
    const onStorage = (/** @type {StorageEvent} */ e) => { if (e.key === STORAGE_KEY) update(); };
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('dv:consent-change', onChange);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  return allowed;
}

/**
 * Devuelve `true` si el user todavía no decidió (no hay consent guardado
 * con la versión actual). Útil para mostrar/ocultar el banner.
 */
export function needsConsentDecision() {
  if (typeof window === 'undefined') return false;
  const c = readConsent();
  // Si no hay consent O la versión es vieja (1.0), pedimos decisión nueva.
  return !c || c.version !== POLICY_VERSION;
}
